import { Tabs } from '@chakra-ui/react';
import { SearchNotificationProcedureDetails } from '@components/cockpit/panels/notificationSearch/SearchNotificationProcedureDetails';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectSensorRoleActionsFromActiveProfile } from '@redux/authent/authent.selectors';
import { canManageNotification } from '@utils/data/notification.utils';
import { createContext, useContext, useMemo } from 'react';

import { NotificationDetails } from '@/types/data/data.types';

import NotificationProcedure from './NotificationProcedure';

type Props = {
  notificationDetails: NotificationDetails;
  isSearch: boolean;
  isFetching: boolean;
};

interface ProcedureTabContextType {
  notificationId: string;
  canManage: boolean;
  isFetching: boolean;
  closable: boolean;
}

const ProcedureTabContext = createContext<ProcedureTabContextType | undefined>(undefined);

export const useProcedureTabContext = (): ProcedureTabContextType => {
  const context = useContext(ProcedureTabContext);
  if (!context) {
    throw new Error('useProcedureTabContext must be used within a ProcedureTabContext.Provider');
  }
  return context;
};

export default function ProcedureTab({ notificationDetails, isSearch, isFetching }: Readonly<Props>) {
  const userActions = useAppSelector(selectSensorRoleActionsFromActiveProfile);

  const { notification, currentProcedure, doneProcedures } = notificationDetails;
  const canManage = canManageNotification(userActions, notification);
  const contextValues = useMemo(
    () => ({ canManage, isFetching, notificationId: notification.id, closable: notification.closable }),
    [canManage, isFetching, notification],
  );

  return (
    <ProcedureTabContext value={contextValues}>
      <Tabs.Content value="procedure">
        {isSearch ? (
          <SearchNotificationProcedureDetails procedures={doneProcedures} />
        ) : (
          <NotificationProcedure
            key={notification.currentStepRank}
            currentStepRank={notification.currentStepRank}
            procedure={currentProcedure!}
          />
        )}
      </Tabs.Content>
    </ProcedureTabContext>
  );
}
